<template>
    <span v-if="visible" class="cdt" :aria-label="`Remain ${mm}:${ss}`" :class="levelClass">
        <div>
            {{ mm }}:{{ ss }}
        </div>
    </span>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

const props = defineProps({
    durationMs: { type: Number, default: 5 * 60 * 1000 },
    reloadOnExpire: { type: Boolean, default: false }, // reload on expire
    autoStart: { type: Boolean, default: false }, // autostart onMount
    hideWhenIdle: { type: Boolean, default: true },
    warnBelow: { type: Number, default: 0.33 },  // 33%
    dangerBelow: { type: Number, default: 0.2 }, // 20%
})

const emit = defineEmits(['expired', 'tick', 'started', 'stopped', 'reset'])

let timerId = null
const deadline = ref(0)
const now = ref(Date.now())
const running = ref(false)

// remaining (always positive)
const remaining = computed(() => Math.max(0, deadline.value - now.value))
const mm = computed(() => String(Math.floor(remaining.value / 60000)).padStart(2, '0'))
const ss = computed(() => String(Math.floor((remaining.value % 60000) / 1000)).padStart(2, '0'))

// normalize lvlvs (0..1)
const toFrac = (v) => (v > 1 ? Math.min(1, Math.max(0, v / 100)) : Math.min(1, Math.max(0, v)))
const warnFrac = computed(() => toFrac(props.warnBelow))
const dangerFrac = computed(() => toFrac(props.dangerBelow))

// part of remaining time
const progress = computed(() => {
  if (!props.durationMs) return 0
  return Math.max(0, Math.min(1, remaining.value / props.durationMs))
})

// классы уровня
const levelClass = computed(() => {
  if (progress.value <= dangerFrac.value) return 'cdt--danger'
  if (progress.value <= warnFrac.value) return 'cdt--warn'
  return 'cdt--ok'
})

// visibility
const visible = computed(() => {
  if (!props.hideWhenIdle) return true // dont show if not started or ended
  if (deadline.value === 0) return false
  if (!running.value && remaining.value === 0) return false
  return true
})

function tick() {
    now.value = Date.now()
    emit('tick', remaining.value)

    if (remaining.value <= 0) {
        stop()
        emit('expired')
        if (props.reloadOnExpire) {
            nextTick(() => window.location.reload()) // extra frame for listeners
        }
    }
}

// interval with drift correction
function loop() {
    // update values every 250ms, but calculate Date.now based
    tick()
    if (!running.value) return
    timerId = setTimeout(loop, 250)
}

function start() {
    // if no deadline - set now as start point
    if (!deadline.value) {
        deadline.value = Date.now() + props.durationMs
    }
    if (running.value) return
    running.value = true
    emit('started', { deadline: deadline.value })
    loop()
}

function stop() {
    running.value = false
    if (timerId) { clearTimeout(timerId); timerId = null }
    emit('stopped')
}

function reset(newDurationMs = props.durationMs) {
    stop()
    deadline.value = Date.now() + newDurationMs
    emit('reset', { deadline: deadline.value })
}

function restart(newDurationMs = props.durationMs) {
    reset(newDurationMs)
    start()
}

// optional: sync from external deadline time
function setDeadline(tsMs) {
    deadline.value = tsMs // tsMs — unix timestamp
}

onMounted(() => {
    if (props.autoStart) {
        reset(props.durationMs)
        start()
    }
})

// background tab: no throttling, just compare with Date.now
function handleVisibility() {
    if (!document.hidden && running.value) tick()
}

document.addEventListener('visibilitychange', handleVisibility)

onBeforeUnmount(() => {
    stop()
    document.removeEventListener('visibilitychange', handleVisibility)
})

defineExpose({ start, stop, reset, restart, setDeadline, running, remaining, mm, ss })
</script>

<style scoped>
.cdt {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  /* border: 1px solid #ddd; */
  width: 56px;
  height: 56px;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.75rem;
  letter-spacing: -0.25px;
  color: #444;
}
.cdt.cdt--ok     { background: #e9f7ef; color: #1a7f37; border-color: #cfe8d7; }   /* зелёный */
.cdt.cdt--warn   { background: #fff8e1; color: #805b10; border-color: #f3e2a6; }   /* жёлтый */
.cdt.cdt--danger { background: #fdecea; color: #b42318; border-color: #f1c1bd; }   /* красный */

</style>
